import { CheckCircle, Edit as EditIcon, ErrorOutline } from '@mui/icons-material';
import {
  Box,
  Chip,
  IconButton,
  LinearProgress,
  Paper,
  Table,
  TableBody,
  TableCell,
  TableContainer,
  TableHead,
  TablePagination,
  TableRow,
  TableSortLabel,
  Tooltip,
  Typography,
} from '@mui/material';
import { format } from 'date-fns';
import { memo } from 'react';

import { translationLib } from 'entities/translation';

import { Pagination, TranslationEntityItem } from 'shared/api';

type Column = {
  id: string;
  label: string;
  sortable: boolean;
  align?: 'left' | 'right' | 'center';
};

const columns: Column[] = [
  { id: 'code', label: 'Code', sortable: true, align: 'left' },
  { id: 'sourceName', label: 'EN — Name', sortable: true, align: 'left' },
  { id: 'sourceDescription', label: 'EN — Description', sortable: false, align: 'left' },
  { id: 'translationName', label: 'Translation — Name', sortable: false, align: 'left' },
  { id: 'translationDescription', label: 'Translation — Description', sortable: false, align: 'left' },
  { id: 'status', label: 'Status', sortable: false, align: 'center' },
  { id: 'updatedAt', label: 'Updated', sortable: true, align: 'left' },
  { id: 'control', label: '', sortable: false, align: 'center' },
];

type Props = {
  items: TranslationEntityItem[];
  pagination: Pagination | null;
  page: number;
  rowsPerPage: number;
  sortBy: string;
  sortOrder: 'asc' | 'desc';
  isLoading: boolean;
  onPageChange: (newPage: number) => void;
  onRowsPerPageChange: (rowsPerPage: number) => void;
  onSortChange: (columnId: string) => void;
  onEdit: (item: TranslationEntityItem) => void;
};

const renderTextCell = (text: string | undefined | null) => {
  if (text === undefined || text === null || text === '') {
    return (
      <Typography variant="body2" color="text.disabled" sx={{ fontStyle: 'italic' }}>
        (empty)
      </Typography>
    );
  }
  const truncated = translationLib.truncate(text);
  return (
    <Tooltip title={text} placement="top-start">
      <Typography
        variant="body2"
        sx={{ whiteSpace: 'nowrap', overflow: 'hidden', textOverflow: 'ellipsis', maxWidth: 280 }}
      >
        {truncated}
      </Typography>
    </Tooltip>
  );
};

const renderTranslationCell = (text: string | undefined) => {
  if (text === undefined) {
    return <Chip color="warning" size="small" label="Missing" />;
  }
  return renderTextCell(text);
};

export const TranslationsTable = memo(
  ({
    items,
    pagination,
    page,
    rowsPerPage,
    sortBy,
    sortOrder,
    isLoading,
    onPageChange,
    onRowsPerPageChange,
    onSortChange,
    onEdit,
  }: Props) => {
    const handleChangePage = (_: unknown, newPage: number) => onPageChange(newPage + 1);
    const handleChangeRowsPerPage = (e: React.ChangeEvent<HTMLInputElement>) =>
      onRowsPerPageChange(parseInt(e.target.value, 10));

    return (
      <Paper
        sx={{ width: '100%', overflow: 'hidden', flex: 1, minHeight: 0, display: 'flex', flexDirection: 'column' }}
      >
        <Box sx={{ width: '100%', height: 4 }}>{isLoading && <LinearProgress />}</Box>

        <Box
          sx={{
            opacity: isLoading ? 0.6 : 1,
            transition: 'opacity 0.2s',
            flex: 1,
            minHeight: 0,
            display: 'flex',
            flexDirection: 'column',
          }}
        >
          <TableContainer sx={{ flex: 1, minHeight: 0 }}>
            <Table stickyHeader size="small" aria-label="translations table">
              <TableHead>
                <TableRow>
                  {columns.map((col) => (
                    <TableCell key={col.id} align={col.align}>
                      {col.sortable ? (
                        <TableSortLabel
                          active={sortBy === col.id}
                          direction={sortBy === col.id ? sortOrder : 'asc'}
                          onClick={() => onSortChange(col.id)}
                        >
                          {col.label}
                        </TableSortLabel>
                      ) : (
                        col.label
                      )}
                    </TableCell>
                  ))}
                </TableRow>
              </TableHead>
              <TableBody>
                {items.length === 0 ? (
                  <TableRow>
                    <TableCell colSpan={columns.length} align="center">
                      No entities match the current filters
                    </TableCell>
                  </TableRow>
                ) : (
                  items.map((item) => (
                    <TableRow key={item.entityId} hover>
                      <TableCell sx={{ fontFamily: 'monospace' }}>{item.code}</TableCell>
                      <TableCell>{renderTextCell(item.source.name)}</TableCell>
                      <TableCell>{renderTextCell(item.source.descriptions)}</TableCell>
                      <TableCell>{renderTranslationCell(item.translation?.name)}</TableCell>
                      <TableCell>{renderTranslationCell(item.translation?.descriptions)}</TableCell>
                      <TableCell align="center">
                        {item.translated ? (
                          <Tooltip title="Translated">
                            <CheckCircle fontSize="small" color="success" />
                          </Tooltip>
                        ) : (
                          <Tooltip title={`Missing: ${item.missingFields.join(', ') || '—'}`}>
                            <ErrorOutline fontSize="small" color="warning" />
                          </Tooltip>
                        )}
                      </TableCell>
                      <TableCell>{item.updatedAt ? format(new Date(item.updatedAt), 'yyyy-MM-dd') : '—'}</TableCell>
                      <TableCell align="center">
                        <IconButton size="small" onClick={() => onEdit(item)} aria-label="edit translation">
                          <EditIcon fontSize="small" />
                        </IconButton>
                      </TableCell>
                    </TableRow>
                  ))
                )}
              </TableBody>
            </Table>
          </TableContainer>
        </Box>

        {pagination && (
          <TablePagination
            rowsPerPageOptions={[25, 50, 100]}
            component="div"
            count={pagination.total}
            rowsPerPage={rowsPerPage}
            page={page - 1}
            onPageChange={handleChangePage}
            onRowsPerPageChange={handleChangeRowsPerPage}
            labelRowsPerPage="Rows per page:"
            labelDisplayedRows={({ from, to, count }) => `${from}-${to} of ${count}`}
            sx={{ borderTop: 1, borderColor: 'divider' }}
          />
        )}
      </Paper>
    );
  },
);

TranslationsTable.displayName = 'TranslationsTable';
